import React, { useState } from 'react';
import { ShieldCheck, CheckCircle2, AlertCircle, FileText, Send, Scale } from 'lucide-react';
import axios from 'axios';

interface TermsOfUseModalProps {
    tenantId: string;
    onAccept: () => void;
}

const TermsOfUseModal: React.FC<TermsOfUseModalProps> = ({ tenantId, onAccept }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleAccept = async () => {
        setLoading(true);
        setError('');
        try {
            await axios.post('/api/admin/accept-terms', { tenant_id: tenantId });
            onAccept();
        } catch (err: any) {
            setError(err.response?.data?.error || 'Erro ao aceitar o contrato. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-surface-900/90 backdrop-blur-md animate-fade-in">
            <div className="bg-white w-full max-w-4xl rounded-[2.5rem] shadow-2xl relative overflow-hidden flex flex-col max-h-[90vh]">
                {/* Header Decorativo */}
                <div className="bg-gradient-to-br from-brand-600 to-brand-800 p-8 text-white relative">
                    <div className="absolute top-0 right-0 p-8 opacity-10">
                        <Scale size={160} />
                    </div>
                    <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                                <FileText size={24} />
                            </div>
                            <span className="text-xs font-black uppercase tracking-[0.2em] opacity-80">Documento de Vinculação Legal</span>
                        </div>
                        <h2 className="text-3xl font-black tracking-tight">Contrato de Prestação de Serviços</h2>
                        <p className="text-brand-100 mt-2 font-medium opacity-90">Leia atentamente as cláusulas e condições de uso da plataforma ZapShop.</p>
                    </div>
                </div>

                {/* Conteúdo do Contrato */}
                <div className="flex-1 overflow-y-auto p-10 custom-scrollbar bg-surface-50">
                    <div className="max-w-3xl mx-auto space-y-10 text-surface-700 leading-relaxed text-sm">

                        <header className="text-center space-y-4 border-b pb-10 border-surface-200">
                            <h1 className="text-xl font-black text-surface-900">CONTRATO DE PRESTAÇÃO DE SERVIÇOS</h1>
                            <p className="text-justify italic">
                                Pelo presente instrumento particular de Contrato de Prestação de Serviços, as partes abaixo qualificadas têm entre si, justo e contratado o presente instrumento:
                            </p>
                            <div className="grid md:grid-cols-2 gap-6 text-left">
                                <div className="bg-white p-4 rounded-2xl border border-surface-200 shadow-sm">
                                    <p className="text-[10px] uppercase font-black text-surface-400 mb-1">Contratado</p>
                                    <p className="font-bold text-surface-900">VINÍCIUS ALVES GONÇALVES DE MATOS</p>
                                    <p className="text-xs">CPF: 112.043.572-22</p>
                                    <p className="text-xs">Rio de Janeiro, RJ</p>
                                </div>
                                <div className="bg-white p-4 rounded-2xl border border-surface-200 shadow-sm">
                                    <p className="text-[10px] uppercase font-black text-surface-400 mb-1">Contratante</p>
                                    <p className="font-bold text-surface-900">DADOS DO LOJISTA REGISTRADO</p>
                                    <p className="text-xs italic">Identificado pelo e-mail e dados vinculados a este painel administrativo.</p>
                                </div>
                            </div>
                        </header>

                        <section className="space-y-4">
                            <h3 className="text-surface-900 font-black uppercase tracking-wider text-xs flex items-center gap-2">
                                <div className="w-2 h-2 bg-brand-500 rounded-full"></div>
                                CLÁUSULA PRIMEIRA – DO OBJETO
                            </h3>
                            <p>
                                1.1. O presente contrato tem como objeto o fornecimento de acesso à plataforma <strong>ZapShop</strong>, que disponibiliza ferramentas para:
                                a) Vitrine digital para exposição de produtos;
                                b) Atendimento automatizado por Inteligência Artificial (IA);
                                c) Disparos de campanhas e gestão de contatos via WhatsApp;
                                d) Sistema de CRM para gestão de pedidos e clientes.
                            </p>
                        </section>

                        <section className="space-y-4">
                            <h3 className="text-surface-900 font-black uppercase tracking-wider text-xs flex items-center gap-2">
                                <div className="w-2 h-2 bg-brand-500 rounded-full"></div>
                                CLÁUSULA SEGUNDA – DA IMPLEMENTAÇÃO E SUPORTE
                            </h3>
                            <p>
                                2.1. O CONTRATADO disponibiliza a infraestrutura tecnológica "como está". O suporte técnico limita-se a configurações da plataforma e correção de eventuais falhas do sistema.
                            </p>
                            <div className="bg-brand-50 border-l-4 border-brand-500 p-4 text-brand-900 rounded-r-2xl font-bold">
                                IMPORTANTE: A prestação de serviço não inclui a entrega da "loja pronta" (com produtos cadastrados, fotos ou descrições). O cadastro, gerenciamento e manutenção do catálogo de produtos é de inteira e exclusiva responsabilidade da CONTRATANTE.
                            </div>
                            <p>
                                2.2. O prazo de suporte técnico e vigência do acesso é condicionado à manutenção da conta ativa, com tempo de resposta de até 48 horas úteis para solicitações.
                            </p>
                        </section>

                        <section className="space-y-4">
                            <h3 className="text-surface-900 font-black uppercase tracking-wider text-xs flex items-center gap-2">
                                <div className="w-2 h-2 bg-brand-500 rounded-full"></div>
                                CLÁUSULA TERCEIRA – DAS RESPONSABILIDADES E ISENÇÃO
                            </h3>
                            <p>
                                3.1. A <strong>ZapShop</strong> é uma ferramenta de intermediação. A CONTRATANTE é a única responsável pelas informações, produtos, valores, pagamentos e logística de entrega.
                            </p>
                            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 text-amber-900 rounded-r-2xl font-bold">
                                3.2. O CONTRATADO não possui qualquer responsabilidade sobre desvios financeiros, inadimplência de clientes finais, danos materiais ou morais decorrentes das transações comerciais realizadas através da plataforma.
                            </div>
                        </section>

                        <section className="space-y-4">
                            <h3 className="text-surface-900 font-black uppercase tracking-wider text-xs flex items-center gap-2">
                                <div className="w-2 h-2 bg-brand-500 rounded-full"></div>
                                CLÁUSULA QUARTA – PROPRIEDADE INTELECTUAL
                            </h3>
                            <p>
                                4.1. Todos os fluxos, scripts, lógicas de IA e códigos-fonte da plataforma são de propriedade intelectual exclusiva do CONTRATADO. A CONTRATANTE possui direito de uso não-exclusivo e intransferível enquanto mantiver sua assinatura ativa.
                            </p>
                        </section>

                        <section className="space-y-4">
                            <h3 className="text-surface-900 font-black uppercase tracking-wider text-xs flex items-center gap-2">
                                <div className="w-2 h-2 bg-brand-500 rounded-full"></div>
                                CLÁUSULA QUINTA – INTELIGÊNCIA ARTIFICIAL (IA)
                            </h3>
                            <p>
                                5.1. A CONTRATANTE reconhece que as soluções de IA podem apresentar "alucinações" ou imprecisões. O CONTRATADO não se responsabiliza por prejuízos decorrentes de respostas imprecisas geradas pelas ferramentas de IA, sendo a supervisão humana obrigatória por parte da CONTRATANTE.
                            </p>
                        </section>

                        <section className="space-y-4">
                            <h3 className="text-surface-900 font-black uppercase tracking-wider text-xs flex items-center gap-2">
                                <div className="w-2 h-2 bg-brand-500 rounded-full"></div>
                                CLÁUSULA SEXTA – NÃO-EXCLUSIVIDADE
                            </h3>
                            <p>
                                6.1. O CONTRATADO declara que é militar da Força Aérea Brasileira (FAB) e, portanto, não oferece serviços em regime de exclusividade, não havendo conflito de interesses com as atividades aqui pactuadas.
                            </p>
                        </section>

                        <section className="space-y-4">
                            <h3 className="text-surface-900 font-black uppercase tracking-wider text-xs flex items-center gap-2">
                                <div className="w-2 h-2 bg-brand-500 rounded-full"></div>
                                CLÁUSULA SÉTIMA – PRIVACIDADE (LGPD)
                            </h3>
                            <p>
                                7.1. Ambas as partes comprometem-se a cumprir a Lei Geral de Proteção de Dados (L. 13.709/2018). O CONTRATADO atuará como operador nas operações de tratamento de dados dos clientes finais iniciadas pela CONTRATANTE.
                            </p>
                        </section>

                        <footer className="pt-6 border-t border-surface-200 text-surface-400 text-[10px] text-justify">
                            Este documento eletrônico possui validade jurídica de contrato de adesão uma vez aceito digitalmente por meio do clique no botão de aceite abaixo, registrando-se o endereço IP, horário e identificador do usuário como prova de anuência.
                        </footer>
                    </div>
                </div>

                {/* Footer com Botão */}
                <div className="p-8 bg-white border-t border-surface-100 flex flex-col gap-4">
                    {error && (
                        <div className="flex items-center gap-2 text-red-600 text-xs font-bold animate-shake bg-red-50 p-3 rounded-xl border border-red-100">
                            <AlertCircle size={16} /> {error}
                        </div>
                    )}

                    <div className="flex items-start gap-4 mb-2">
                        <div className="w-6 h-6 shrink-0 rounded-lg flex items-center justify-center bg-brand-100 text-brand-600">
                            <ShieldCheck size={18} />
                        </div>
                        <div className="space-y-1">
                            <p className="text-[11px] text-surface-900 font-black uppercase tracking-wider leading-tight">
                                Declaração de Ciência e Aceite Digital
                            </p>
                            <p className="text-[10px] text-surface-400 font-medium leading-relaxed">
                                Ao clicar em "Assinar Digitalmente e Acessar", o lojista confirma que leu, compreendeu e concorda com todas as cláusulas do <strong>Contrato de Prestação de Serviços ZapShop</strong> acima, inclusive quanto à isenção de responsabilidade sobre desvios financeiros e a obrigação do cadastro de catálogo por conta própria.
                            </p>
                        </div>
                    </div>

                    <button
                        onClick={handleAccept}
                        disabled={loading}
                        className="w-full bg-surface-900 hover:bg-black text-white font-black py-5 rounded-[1.5rem] shadow-2xl transition-all active:scale-[0.98] flex items-center justify-center gap-3 text-lg"
                    >
                        {loading ? (
                            <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                            <>
                                Assinar Digitalmente e Acessar Painel <Send size={20} className="text-brand-400" />
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TermsOfUseModal;
